import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UsersService } from '../../services/users.service';
import { PermissionService } from '../../services/permission.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  loading = false;
  submitted = false;
  error = '';
  returnUrl!: string;
  showForgotPasswordModal = false;
  forgotPasswordSubmitted = false;
  forgotPasswordLoading = false;
  forgotPasswordMessage = '';
  forgotPasswordError = '';
  lastLoggedEmail: string | null = null;
  lastLoggedUsername: string | null = null;
  lastLoggedAvatar: string | null = null;
  showPassword = false;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private usersService: UsersService,
    private permissionService: PermissionService
  ) {
    // Redirect to dashboard if already logged in
    if (this.authService.currentUserValue) {
      this.router.navigate(['/dashboard']);
    }
  }

  ngOnInit(): void {
    // Recuperar o último email logado
    const lastEmail = this.authService.getLastLoggedEmail();
    this.lastLoggedEmail = lastEmail;
    
    // Recuperar o último username (se disponível no localStorage)
    const lastUsername = this.authService.getLastLoggedUsername();
    this.lastLoggedUsername = lastUsername;
    
    // Recuperar o avatar do último usuário logado
    const lastAvatar = localStorage.getItem('lastLoggedAvatar');
    this.lastLoggedAvatar = lastAvatar;
    
    // Se tiver userId salvo, tentar buscar avatar atualizado
    const lastUserId = localStorage.getItem('lastLoggedUserId');
    if (lastUserId && !lastAvatar) {
      this.loadLastUserAvatar(parseInt(lastUserId));
    }
    
    this.loginForm = this.formBuilder.group({
      email: [lastEmail || '', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      captcha: [false, Validators.requiredTrue]
    });

    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });

    // Get return url from route parameters or default to dashboard
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
  }

  // Convenience getter for easy access to form fields
  get f() { return this.loginForm.controls; }
  get fp() { return this.forgotPasswordForm.controls; }

  // Check if forgot password email is valid
  get isForgotPasswordEmailValid(): boolean {
    const emailControl = this.forgotPasswordForm.get('email');
    return emailControl ? emailControl.valid : false;
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.f['captcha'].invalid) {
      this.f['captcha'].markAsTouched();
      return;
    }

    // Stop if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.error = '';

    this.authService.login(this.f['email'].value, this.f['password'].value)
      .subscribe({
        next: (user) => {
          if (user) {
            this.handleSuccessfulLogin(user);
            return;
          }

          this.tryTemporaryPasswordLogin(
            this.f['email'].value,
            this.f['password'].value
          );
        },
        error: () => {
          this.error = 'Erro ao fazer login. Tente novamente.';
          this.loading = false;
        }
      });
  }

  private handleSuccessfulLogin(user: { userId?: number }): void {
    if (user.userId) {
      this.usersService.getAvatar(user.userId).subscribe({
        next: (response) => {
          if (response && response.avatar) {
            const avatarData = response.avatar.startsWith('data:image')
              ? response.avatar
              : `data:image/png;base64,${response.avatar}`;
            localStorage.setItem('lastLoggedAvatar', avatarData);
          }
        },
        error: () => {}
      });
    }

    this.permissionService.loadGroupsFromToken();
    this.router.navigate([this.returnUrl]);
  }

  private tryTemporaryPasswordLogin(email: string, password: string): void {
    this.usersService.validateTemporaryPasswordLogin(email, password, { silent: true }).subscribe({
      next: (response) => {
        if (response.success && response.requestedPassword) {
          this.loading = false;
          this.router.navigate(['/reset-password'], {
            queryParams: { email, mode: 'temporary' }
          });
          return;
        }

        this.error = 'Usuário ou senha inválidos';
        this.loading = false;
      },
      error: () => {
        this.error = 'Usuário ou senha inválidos';
        this.loading = false;
      }
    });
  }

  openForgotPasswordModal(): void {
    this.showForgotPasswordModal = true;
    this.forgotPasswordSubmitted = false;
    this.forgotPasswordMessage = '';
    this.forgotPasswordError = '';
    this.forgotPasswordForm.reset();
  }

  closeForgotPasswordModal(): void {
    this.showForgotPasswordModal = false;
    this.forgotPasswordForm.reset();
    this.forgotPasswordSubmitted = false;
    this.forgotPasswordMessage = '';
    this.forgotPasswordError = '';
  }

  onForgotPasswordSubmit(): void {
    this.forgotPasswordSubmitted = true;
    this.forgotPasswordMessage = '';
    this.forgotPasswordError = '';

    // Stop if form is invalid
    if (this.forgotPasswordForm.invalid) {
      return;
    }

    this.forgotPasswordLoading = true;

    const emailValue = this.fp['email'].value;
    
    this.authService.requestPasswordReset(emailValue)
      .subscribe({
        next: (response) => {
          this.forgotPasswordLoading = false;
          if (response.success) {
            this.forgotPasswordMessage = 'Email de recuperação enviado com sucesso! Redirecionando...';
            setTimeout(() => {
              this.closeForgotPasswordModal();
              this.router.navigate(['/reset-password-code'], { queryParams: { email: emailValue } });
            }, 2000);
          } else {
            this.forgotPasswordError = response.message || 'Erro ao enviar email de recuperação';
          }
        },
        error: (error) => {
          this.forgotPasswordLoading = false;
          this.forgotPasswordError = 'Erro ao enviar email de recuperação. Tente novamente.';
        }
      });
  }

  navigateToResetPasswordCode(): void {
    const emailValue = this.fp['email'].value;
    this.closeForgotPasswordModal();
    this.router.navigate(['/reset-password-code'], { queryParams: { email: emailValue } });
  }

  // Método para carregar o avatar do último usuário
  loadLastUserAvatar(userId: number): void {
    this.usersService.getAvatar(userId).subscribe({
      next: (response) => {
        if (response && response.avatar) {
          const avatarData = response.avatar.startsWith('data:image') 
            ? response.avatar 
            : `data:image/png;base64,${response.avatar}`;
          this.lastLoggedAvatar = avatarData;
          localStorage.setItem('lastLoggedAvatar', avatarData);
        }
      },
      error: (error) => {
      }
    });
  }

  // Método para remover o último email salvo
  removeLastLoggedEmail(): void {
    this.authService.clearLastLoggedEmail();
    this.lastLoggedEmail = null;
    this.lastLoggedUsername = null;
    this.lastLoggedAvatar = null;
    localStorage.removeItem('lastLoggedAvatar');
    localStorage.removeItem('lastLoggedUserId');
    this.loginForm.patchValue({ email: '' });
  }

  // Método para obter as iniciais do nome do usuário
  getUserInitials(): string {
    if (this.lastLoggedUsername) {
      const names = this.lastLoggedUsername.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    }
    if (this.lastLoggedEmail) {
      return this.lastLoggedEmail.substring(0, 2).toUpperCase();
    }
    return 'US';
  }
}
